import {
  toRaw,
  isRef,
  isReactive,
  isProxy,
} from 'vue';
import {isBefore, startOfDay} from "date-fns";
import 'temporal-polyfill/global';
import {getProjectStatus, getSpecialEventConfig, languages} from "@/helpers/constants.js";

export function getBrowserLanguage() {
  const language = navigator.language.substring(0, 2).toLowerCase();
  return languages.indexOf(language) !== -1 ? language : 'en';
}

export function deepToRaw(sourceObj) {
  const objectIterator = (input) => {
    if (Array.isArray(input)) {
      return input.map((item) => objectIterator(item));
    }
    if (isRef(input) || isReactive(input) || isProxy(input)) {
      return objectIterator(toRaw(input));
    }
    if (input && typeof input === 'object') {
      return Object.keys(input).reduce((acc, key) => {
        acc[key] = objectIterator(input[key]);
        return acc;
      }, {});
    }
    return input;
  };

  return objectIterator(sourceObj);
}

export function sortRank(a, b) {
  const aRank = a.rank ?? 'Z';
  const bRank = b.rank ?? 'Z';
  return aRank !== bRank ? aRank.localeCompare(bRank) : a.rank_number - b.rank_number;
}

export function sortNullLast(a, b) {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return (typeof a !== 'number') ? a.localeCompare(b) : a - b;
}

function getEventConflicts(event) {
  return event.employees.filter(employee => employee.conflict);
}

export function eventHasInternalConflicts(event) {
  const conflicts = getEventConflicts(event);
  return conflicts.length > 0 && conflicts.every(conflict => conflict.conflict.internal);
}

export function eventHasExternalConflicts(event) {
  const conflicts = getEventConflicts(event);
  return conflicts.length > 0 && conflicts.every(conflict => !conflict.conflict.internal);
}

export function eventHasDeclines(event) {
  return event.employees.some(employee => employee.responseStatus === 'declined');
}

export function isEventInPast (event) {
  const eventDate = startOfDay(new Date(event.start));
  const today = startOfDay(new Date());
  return isBefore(eventDate, today);
}

export function dateInPast (date) {
  const eventDate = startOfDay(new Date(date));
  const today = startOfDay(new Date());
  return isBefore(eventDate, today);
}

export function isOvernightEvent(event) {
  return event.type === 'overnight';
}

const eventTypeOrder = { work: 0, blast: 1, container_drop: 2, container_pickup: 3, overnight: 4 };

// Events run in date order. An overnight stay starts at 00:00, but belongs after the day it covers.
export function compareEvents(a, b) {
  const dayA = a.start.split(' ')[0];
  const dayB = b.start.split(' ')[0];
  if (dayA !== dayB) {
    return dayA < dayB ? -1 : 1;
  }

  if (isOvernightEvent(a) !== isOvernightEvent(b)) {
    return isOvernightEvent(a) ? 1 : -1;
  }

  const startA = new Date(a.start).getTime();
  const startB = new Date(b.start).getTime();
  if (startA !== startB) {
    return startA - startB;
  }

  return eventTypeOrder[a.type] - eventTypeOrder[b.type];
}

export function projectHasFTEError(project, event) {
  return (event.fte || project.fte) > event.employees.length;
}

export function isValidTime(timeString) {
  const timeRegex = /^(?:2[0-3]|[01]?[0-9]):[0-5][0-9]$/;
  return timeRegex.test(timeString);
}

export function timeInMinutes(timeString) {
  if (!timeString || !timeString.includes(':')) return null;

  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
}

export function isNextDay(timeString) {
  if (!timeString || !timeString.includes(':')) return false;
  const [hours] = timeString.split(':').map(Number);
  return hours >= 24;
}

export function castTimeForDisplay(timeString) {
  if (!timeString || !timeString.includes(':')) return timeString;
  const [hours, minutes, seconds] = timeString.split(':').map(Number);

  // Normalize hours to 24-hour format (26:00:00 becomes 02:00:00)
  const normalizedHours = hours % 24;

  // Pad with leading zeros to maintain HH:MM:SS format
  const paddedHours = normalizedHours.toString().padStart(2, '0');
  const paddedMinutes = minutes.toString().padStart(2, '0');
  const paddedSeconds = (seconds) ? seconds.toString().padStart(2, '0') : '00';

  return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
}

export function castTimeToNextDay(timeString) {
  const [hours, minutes] = timeString.split(':').map(Number);

  // Add 24 hours to shift time to next day (02:00:00 becomes 26:00:00)
  const nextDayHours = hours + 24;

  // Pad with leading zeros to maintain HH:MM:SS format
  const paddedHours = nextDayHours.toString().padStart(2, '0');
  const paddedMinutes = minutes.toString().padStart(2, '0');

  return `${paddedHours}:${paddedMinutes}`;
}

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was invoked.
 * The debounced function comes with a `cancel` method to cancel delayed `func` invocations.
 *
 * @param {Function} func The function to debounce.
 * @param {number} wait The number of milliseconds to wait.
 * @param {boolean} [immediate=false] Specify whether `func` should be invoked on the leading edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 */
 export function debounce(func, wait, immediate = false) {
  let timeout;
  let result;

  const debounced = function(...args) {
    const context = this;
    const later = function() {
      timeout = null;
      if (!immediate) {
        result = func.apply(context, args);
      }
    };

    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) {
      result = func.apply(context, args);
    }

    return result;
  };

  debounced.cancel = function() {
    clearTimeout(timeout);
    timeout = null;
  };

  return debounced;
}

export function toCalendarDate(dateString) {
  if (!dateString) {
    return;
  }

  if (!/[T:]/.test(dateString)) {
    return Temporal.PlainDate.from(dateString);
  }

  // Replace space with T for ISO format and add Europe/Amsterdam timezone
  const isoString = dateString.replace(' ', 'T');
  return Temporal.ZonedDateTime.from(isoString + '[UTC]');
}

export function getProjectColor(project, event, t) {
   if (project.void || !t) {
      return 'grey';
   }
   if (event?.type && event.type !== 'work') {
      return getSpecialEventConfig(t)[event.type]?.markerColor ?? 'indufastYellow';
   }
   return getProjectStatus(t).find(status => status.value === project.status)?.color || 'primary';
}

export function timeInSeconds(timeString) {
  if (!timeString || !timeString.includes(':')) return 0;
  const parts = timeString.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 3600 + parts[1] * 60;
  }
  return 0;
}

export const getLandingRoute = (usergroup) => {
  return { name: usergroup === 'EMPLOYEE' ? '/material/' : '/dashboard' };
};
